import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

type FontWeight = 'bold' | 'medium' | 'regular' | 'semibold';
type Align = 'left' | 'right' | 'center';
type Size = 'display' | 'headline' | 'title-lg' | 'title'
    | 'body-short' | 'body-long' | 'label-lg' | 'label-sm';

interface Props extends HTMLAttributes<HTMLElement>{
    fontWeight?: FontWeight;
    color?: string;
    align?: Align;
    ellipsis?: boolean;
    size?: Size;
    as?: 'p' | 'span' | 'div' | 'h1' |'h2' | 'h3' | 'h4' |'h5' | 'h6' | 'dt' | 'dd';
    multiline?: boolean;
}

const FONT_WEIGHTS: Record<FontWeight, number> = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};

/**
 * index.scss 의 --font-size-* 토큰을 그대로 사용한다.
 * 실제 px 값은 index.scss 에서만 관리하며, 640px 이상에서 자동으로 확대된다.
 */
const FONT_SIZES: Record<Size, string> = {
    display: 'var(--font-size-display)',
    headline: 'var(--font-size-headline)',
    'title-lg': 'var(--font-size-title-lg)',
    title: 'var(--font-size-title)',
    'body-short': 'var(--font-size-body-short)',
    'body-long': 'var(--font-size-body-long)',
    'label-lg': 'var(--font-size-label-lg)',
    'label-sm': 'var(--font-size-label-sm)',
};

function Text(props: Props) {
    const {
        children, fontWeight, color, align = 'left', ellipsis = false,
        as = 'p',
        size = 'body-short', multiline = false, ...rest
    } = props;
    const { i18n: { language } } = useTranslation();
    return (
        <Component
            as={as}
            lang={language}
            fontWeight={fontWeight}
            textColor={color}
            align={align}
            ellipsis={ellipsis}
            size={size}
            multiline={multiline}
            {...rest}>
            {children}
        </Component>
    );
}

const STYLE_PROPS = ['as', 'fontWeight', 'textColor', 'align', 'ellipsis', 'size', 'multiline'];

const Component = styled('p', {
    shouldForwardProp: (prop) => !STYLE_PROPS.includes(prop),
})<{
    fontWeight?: FontWeight;
    textColor?: string;
    align: Align;
    ellipsis: boolean;
    size: Size;
    multiline?: boolean;
}>`
    color: ${({ textColor }) => textColor || 'var(--color-gray9)'};
    font-size: ${({ size }) => FONT_SIZES[size]};
    font-weight: ${({ fontWeight }) => (fontWeight ? FONT_WEIGHTS[fontWeight] : 400)};
    line-height: 1.4;
    word-break: keep-all;
    text-align: ${({ align }) => align};

    ${({ multiline }) => multiline && css`
    white-space: pre-wrap;
    word-break: break-word;
    `}


    ${({ ellipsis }) => ellipsis && css`
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `}
`;

export type { Size as TextSize };
export default Text;
